import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { post, get, del, patch } from '@api/httpClient';
import { AxiosResponse } from 'axios';
import { APIError } from '../types';
import { GenePanelCartPayload, GenePanelResultSchema } from '@/zod_schemas';

const QUERY_KEY = {
  GENEPANELS: ['gene_panel'], // Array is valid for QueryKey
  GENEPANEL: (panelId: string) => ['gene_panel', { id: panelId }],
};

export const useGetGenePanel = (panelId?: string) =>
  useSuspenseQuery<GenePanelCartPayload>({
    queryKey: QUERY_KEY.GENEPANEL(panelId || ''),
    queryFn: async () => {
      if (!panelId) return {};

      const resp = await get(`/gene_panels/${panelId}`);
      return resp.data;
    },
  });

// export const useCreateGenePanel = () =>
//   useMutation<AxiosResponse<GenePanelSchema>, APIError, GenePanelSchema>({
//     mutationFn: (genePanel) =>
//       post<GenePanelSchema>('/gene_panels/', { ...genePanel }),
//   });

const QUERY_KEY_LIST_GENE_PANEL = {
  GENEPANELS: ['gene_panel_lists'], // Array is valid for QueryKey
};

export const useCreateGenePanel = () => {
  const queryClient = useQueryClient(); // Get access to the query client

  return useMutation<
    AxiosResponse<GenePanelCartPayload>,
    APIError,
    GenePanelCartPayload
  >({
    mutationFn: (genePanel) =>
      post<GenePanelCartPayload>('/gene_panels/', { ...genePanel }),

    onSuccess: () => {
      // Use the query key array for invalidation
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_LIST_GENE_PANEL.GENEPANELS, // Correct usage here
      });
    },
  });
};

export const useListGenePanel = () =>
  useSuspenseQuery<GenePanelResultSchema>({
    queryKey: QUERY_KEY_LIST_GENE_PANEL.GENEPANELS, // Now this is a string, which is valid
    queryFn: async () => {
      const resp = await get<GenePanelResultSchema>('/gene_panels/');
      return resp.data;
    },
  });

type AddGenesInput = {
  panelId: string; // the created panel id
  genes: Array<{ gene_id: string; notes?: string }>;
};

type AddGenesResp = { genes: Array<{ gene_id: string; notes: string }> };

export const useAddGenes = () =>
  useMutation<AxiosResponse<AddGenesResp>, APIError, AddGenesInput>({
    mutationFn: ({ panelId, genes }) =>
      post<AddGenesResp>(`/gene_panels/${panelId}/add_gene/`, { genes }),
  });

type DeletePayload = { id: string };

export const useDeleteGenePanel = () => {
  const qc = useQueryClient();

  return useMutation<AxiosResponse<void>, APIError, DeletePayload>({
    mutationFn: ({ id }) => del(`/gene_panels/${id}/`),

    // keep lists fresh
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['gene_panels'] });
    },
  });
};

type DeletePayload2 = { panelId: string; id: string };
export const useDeleteGene = () => {
  const qc = useQueryClient();

  return useMutation<AxiosResponse<void>, APIError, DeletePayload2>({
    mutationFn: ({ panelId, id }) => {
      // normalize CSV (trim spaces, drop empties)
      const geneIds = id
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
        .join(',');

      return del(`/gene_panels/${panelId}/remove_gene/`, {
        params: { gene_ids: geneIds },
      });
    },

    onSuccess: async (_res, { panelId }) => {
      // keep relevant caches fresh (tweak keys to your app)
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['gene_panel', panelId] }),
        qc.invalidateQueries({ queryKey: ['gene_panels'] }),
      ]);
    },
  });
};

export const usePatchGenePanel = () =>
  useMutation<
    AxiosResponse<GenePanelCartPayload>, // adjust if your hook returns plain object
    APIError,
    {
      id: string;
      payload: Partial<
        Pick<GenePanelCartPayload, 'name' | 'description' | 'version'>
      >;
    }
  >({
    mutationFn: ({ id, payload }) => patch(`/gene_panels/${id}/`, payload),
  });

export const useGetAllGenePanels = () =>
  useSuspenseQuery<GenePanelResultSchema>({
    queryKey: QUERY_KEY_LIST_GENE_PANEL.GENEPANELS, // Now this is a string, which is valid
    queryFn: async () => {
      const resp = await get<GenePanelResultSchema>('/gene_panels/');
      return resp.data;
    },
  });
